import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { AppBar, Box, Button, Dialog, Grid, IconButton, Step, StepButton, Stepper, Toolbar, Typography } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import _, { cloneDeep } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { useProgress } from '@/contexts/ProgressProvider';
import { useModalContext } from '@/modals/VNModalProvider';

import MetadataContent from '../../../components/MetadataContent';
import { ClipGroupStep } from '../../../enums';
import type { Item } from '../../../models';
import { CustomAttributes } from '../../../models/customAttributes';
import type { ClipGroup } from '../../../models/item';
import { ValidationErrors } from '../../../models/ValidationErrors';
import { FuelEntityType, FuelPortalService } from '../../../services';
import { useAppDispatch } from '../../../store';
import { AssetForm } from '../../../store/forms';
import { applicationDataSelector } from '../../../store/slices/application-data';
import { UpdateAssetForm } from '../../../store/slices/asset-editor';
import { fuelUserAccessSliceSelector } from '../../../store/slices/fuelUserAccess';
import { toSaveAssetRequest } from '../../../store/slices/saveAssetJobsState/toSaveAssetRequest';
import useAppUtils from '../../../utils/appUtils';
import itemUtils from '../../../utils/itemUtils';
import AssetDetailsModal from '../asset-details-modal/AssetDetailsModal';
import { enhanceFetchedAsset, enhanceFetchedAssets } from '../asset-listing/enhanceFetchedAsset';
import { validateAssetForm } from '../validateAssetForm';
import ClipGroupContent from './ClipGroupContent';
import ClipGroupInfo from './ClipGroupInfo';
import { generateNewAssetForm, toSaveClipGroupRequest } from './toSaveClipGroupRequest';
import { validateClipGroupContent } from './validateClipGroupContent';

interface IProps {
  open: boolean;
  onClose: () => void;
  processAssetsReturnedFromSaving?: ((newAssets: Array<Item>) => void) | null;
  assetForm: AssetForm;
}
interface Step {
  id: ClipGroupStep;
  title: string;
}
const infoStep = { id: ClipGroupStep.Info, title: 'Info' };
const contentStep = { id: ClipGroupStep.Content, title: 'Clips' };
const ClipGroupModal = ({ open, onClose, processAssetsReturnedFromSaving = null, assetForm }: IProps) => {
  const { ouData } = useSelector(applicationDataSelector);
  const { permissions, fuelUserAccessData } = useSelector(fuelUserAccessSliceSelector);
  const flags = useFlags();
  // const { assetForm } = useSelector(assetEditorSelector);
  const [activeStep, setActiveStep] = useState(0);
  // const [skipped, setSkipped] = useState(new Set<number>());
  const [availableSteps, setAvailableSteps] = useState<Array<Step>>([]);
  const [currentStep, setCurrentStep] = useState<Step>(infoStep);
  const [completed, setCompleted] = React.useState({});
  const { vnConfirm, alert } = useAppUtils();
  const [clipGroup, setClipGroup] = useState<ClipGroup | null>(null);
  const [rightOpen, setRightOpen] = React.useState(false);
  const [tagState, setTagState] = useState(CustomAttributes.Create());
  const tagStateRef = useRef(CustomAttributes.Create());
  const [openAssetDetailsModal, setOpenAssetDetailsModal] = useState(false);
  const modal = useModalContext();
  const { setProgress } = useProgress();

  const dispatch = useAppDispatch();

  const assetFormValues = useForm<AssetForm>({
    defaultValues: useMemo(() => {
      // console.log('assetForm', assetForm)
      return assetForm;
    }, [assetForm]),
  });

  const handleDrawerClose = async () => {
    const isOk = await vnConfirm('Are you sure you want to cancel?  Any unsaved changes will be lost.');
    if (isOk) doClose();
  };

  const doClose = () => {
    onClose();
    reset();
  };

  const reset = () => {
    setActiveStep(0);
    setCompleted({});
    setCurrentStep(infoStep);
  };
  const onInputChange = (event) => {
    // console.log('onInputChange', event);
    if (event.key === ',' || event.key === '\\') {
      event.preventDefault();
      alert('Commas and backslashes not allowed in tag names.', 'warning');
      return;
    }
    const myState = _.cloneDeep(tagStateRef.current);
    myState[event.target.name] = event.target.value;
    tagStateRef.current = myState;
    setTagState(myState);
  };
  const onSingleTagFieldBlur = (event) => {
    // console.log('onCustomTagFieldBlur2', event);
    const existingTags: string[] = assetFormValues.getValues('tags');
    const newTags = itemUtils.onSingleTagFieldBlur(event, existingTags);
    assetFormValues.setValue('tags', newTags, { shouldValidate: true });
    refreshLongFormFields(newTags);
  };
  const onMultipleTagFieldBlur = (event) => {
    const existingTags: string[] = assetFormValues.getValues('tags');
    const newTags = itemUtils.onMultipleTagFieldBlur(event, existingTags);
    // console.log('onCustomTagFieldBlur newTags', newTags);
    assetFormValues.setValue('tags', newTags, { shouldValidate: true });
    refreshLongFormFields(newTags);
  };
  const refreshLongFormFields = (tags) => {
    const myState = itemUtils.extractCustomAttributesFromTags(tags, tagStateRef.current);
    // console.log('refreshLongFormFields', tags);
    tagStateRef.current = myState;
    setTagState(myState);
  };
  const populateLongFormFields = (assetForm) => {
    // console.log('populateLongFormFields', assetForm);
    refreshLongFormFields(assetForm.tags);
  };
  const getStepContent = (step: Step) => {
    switch (step.id) {
      case ClipGroupStep.Info:
        return (
          <ClipGroupInfo
            assetFormValues={assetFormValues}
            assetForm={assetForm}
            isNew
            onSaveClick={onSaveClick}
            onCancelClick={onCancelClick}
            onFormChanged={onFormChanged}
            rightOpen={rightOpen}
            expandRightSide={expandRightSide}
          />
        );
      case ClipGroupStep.Content:
        return (
          <ClipGroupContent
            assetFormValues={assetFormValues}
            onFormChanged={onFormChanged}
            openEditPopup={openAssetEditorModal}
          />
        );
      default:
        return 'Unknown step';
    }
  };
  const totalSteps = () => {
    return availableSteps.length;
  };

  const completedSteps = () => {
    return Object.keys(completed).length;
  };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const allStepsCompleted = () => {
    return completedSteps() === totalSteps();
  };

  const handleNext: SubmitHandler<AssetForm> = () => {
    const newActiveStepNum =
      isLastStep() && !allStepsCompleted()
        ? // It's the last step, but not all
          // steps have been completed,
          // find the first step that has been completed
          availableSteps.findIndex((step, i) => !(i in completed))
        : activeStep + 1;
    const form: AssetForm = assetFormValues.getValues();
    const nextStep = availableSteps[newActiveStepNum];
    const isOk = validateCurrentStep(form, nextStep);
    // console.log('form', form);
    if (isOk) {
      // assetFormValues.setValue('errors', []);
      setActiveStep(newActiveStepNum);
      setCurrentStep(nextStep);
    }
  };
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    const nextStep = availableSteps[activeStep - 1];
    setCurrentStep(nextStep);
  };

  const handleStep = (step) => () => {
    const form: AssetForm = assetFormValues.getValues();
    const nextStep = availableSteps[step];
    const isOk = validateCurrentStep(form, nextStep);
    if (isOk) {
      setActiveStep(step);
      setCurrentStep(nextStep);
    }
  };
  const handleCancel = () => {};
  const onFormChanged = () => {
    const form: AssetForm = cloneDeep(assetFormValues.getValues());
    const errors = validateForm(form);
    form.errors = errors;
    assetFormValues.setValue('errors', errors);
    // dispatch(CreatAssetForm(form));
    populateLongFormFields(form);
  };

  const resetForm = () => {};

  const onCancelClick = () => {
    // resetForm();
    // alert('Form reset');
  };

  const onSaveClick: SubmitHandler<AssetForm> = async (form: AssetForm) => {
    const formData = cloneDeep(form);
    const errors = validateForm(form);
    if (errors.hasAny()) {
      showErrorMessage(formData, errors);
      return;
    }
    setProgress(true, 'Saving Clip Group...');
    const clipGroupReq = toSaveClipGroupRequest(formData, ouData.ouId, clipGroup);
    try {
      const clipGroup = await FuelPortalService.upsertEntity(FuelEntityType.ClipGroup, clipGroupReq);
      if (clipGroup) {
        // Update clipgroup asset metadata
        let payload = toSaveAssetRequest(formData, ouData.schemas, ouData.categories, fuelUserAccessData, ouData);
        if (formData.mode === 'new') {
          const clipGroupAssetForm = await createAssetForm(clipGroup.id);
          const newAssetForm = generateNewAssetForm(formData, clipGroupAssetForm);
          // dispatch(UpdateAssetForm(newAssetForm));
          payload = toSaveAssetRequest(newAssetForm, ouData.schemas, ouData.categories, fuelUserAccessData, ouData);
        }
        setProgress(true, 'Saving Clip Group...');
        try {
          const newAsset = await FuelPortalService.updateAsset(payload);
          if (newAsset) {
            if (processAssetsReturnedFromSaving && formData.mode === 'existing') processAssetsReturnedFromSaving([newAsset]);
            alert('Clip Group successfully saved.');
            setProgress(false);
            doClose();
          }
        } catch (ex) {
          console.error('Unable to save Clip: Some error occured while trying to save.  Please contact support', ex);
          alert('Unable to save Clip: Some error occured while trying to save.  Please contact support', 'error');
          setProgress(false);
        }
      }
    } catch (ex) {
      console.error('Unable to save Clip Gorup: Some error occured while trying to save.  Please contact support', ex);
      alert('Unable to save Clip Gorup  Error: Some error occured while trying to save.  Please contact support', 'error');
      setProgress(false);
    }
  };

  const createAssetForm = async (id: string): Promise<AssetForm> => {
    // console.log('createAssetForm', { id });
    try {
      let asset = await FuelPortalService.getClipById(id);
      asset = enhanceFetchedAsset(asset, permissions, flags, false, [], [], ouData);
      if (asset) {
        const form = await AssetForm.createFromData(asset, permissions, ouData);
        // dispatch(UpdateAssetForm(form));
        return form;
      }
      return null;
    } catch (ex) {
      alert('Error fetching asset.', 'error');
      console.error('Error fetching asset', ex);
      setProgress(false);
      return null;
    }
  };

  const showErrorMessage = (form, errors) => {
    const formData = cloneDeep(form);
    formData.errors = errors;
    assetFormValues.setValue('errors', errors, { shouldValidate: true });
    // dispatch(UpdateAssetForm(formData));
    const message = `Please fix validation errors before continuing: ${errors.first().message}`;
    alert(message, 'error');
  };

  const validateCurrentStep = (form: AssetForm, step?: Step): boolean => {
    const nextStepIndex = availableSteps.indexOf(step);
    const formData = cloneDeep(form);

    const validate = () => {
      if (currentStep) {
        for (let i = 0; i < nextStepIndex; i++) {
          const myStep = availableSteps[i];
          let validationErrors = new ValidationErrors([]);
          switch (myStep.id) {
            case infoStep.id:
              validationErrors = validateAssetForm(formData);
              break;
            case contentStep.id:
              validationErrors = validateClipGroupContent(formData);
              break;
            default:
              console.error(`Unexpected step: ${myStep.id}`);
              break;
          }
          if (validationErrors.hasAny()) return validationErrors;
        }
        return new ValidationErrors([]);
      }
      let validationErrors = validateAssetForm(formData);
      if (validationErrors.hasAny()) return validationErrors;

      validationErrors = validateClipGroupContent(formData);
      if (validationErrors.hasAny()) return validationErrors;

      return new ValidationErrors([]);
    };
    const errors = validate();
    if (errors.hasAny()) {
      showErrorMessage(formData, errors);
      return false;
    }
    return true;
  };

  const validateForm = (form: AssetForm) => {
    let validationErrors = validateAssetForm(form);
    if (validationErrors.hasAny()) return validationErrors;

    validationErrors = validateClipGroupContent(form);
    if (validationErrors.hasAny()) return validationErrors;

    return new ValidationErrors([]);
  };

  const getClips = async () => {
    setProgress(true);
    try {
      const clipGroup = await FuelPortalService.getEntityById(FuelEntityType.ClipGroup, assetForm.id);
      if (clipGroup && clipGroup.clipIds.length > 0) {
        setClipGroup(clipGroup);
        let clips: Item[] = await FuelPortalService.batchGet(FuelEntityType.Clip, clipGroup.clipIds);
        if (clips && clips.length > 0) {
          // setClips(enhanceFetchedAssets(clips, permissions, flags, true, [], [], ouData));
          clips = clips.filter((c) => !c.isDeleted);
          const ids = clips.map((item) => item.id);
          assetFormValues.setValue('clipIds', ids);
          assetFormValues.setValue(
            'clipGroupContentClips',
            enhanceFetchedAssets(clips, permissions, flags, true, [], [], ouData)
          );
        }
        setProgress(false);
      }
    } catch (ex) {
      setProgress(false);
      alert('Error fetching batch get of clips.', 'error');
    }
  };

  const expandRightSide = (val) => {
    // console.log('val', val);
    setRightOpen(!rightOpen);
    assetFormValues.setValue('isLongFormContentEnabled', val);
  };

  const openAssetEditorModal = async (event, item: Item) => {
    // console.log('item', item);
    if (item && item.id) {
      const value = await createAssetForm(item.id);
      if (value) {
        dispatch(UpdateAssetForm(value));
      }
      // console.log('value', value);
      // if (flags['enable-view-nexa-new-ui-design']) {
      //   console.log('flags', flags['enable-view-nexa-new-ui-design']);
      //   modal.onOpen('Edit Asset', 'xl', <VNAssetEditor addToList={refreshAssets} />);
      // } else {
      //   setOpenAssetDetailsModal(true);
      // }
      setOpenAssetDetailsModal(true);
    }
  };

  const refreshAssets = async (items: Item[]) => {
    // console.log('items', items)
    try {
      setProgress(true);
      const clipIds = assetFormValues.getValues('clipIds');
      if (clipIds.length > 0) {
        let clips: Item[] = await FuelPortalService.batchGet(FuelEntityType.Clip, clipIds);
        if (clips && clips.length > 0) {
          // setClips(enhanceFetchedAssets(clips, permissions, flags, true, [], [], ouData));
          clips = clips.filter((c) => !c.isDeleted);
          const ids = clips.map((item) => item.id);
          assetFormValues.setValue('clipIds', ids);
          const enhancedAssets = enhanceFetchedAssets(clips, permissions, flags, true, [], [], ouData);
          assetFormValues.setValue('clipGroupContentClips', enhancedAssets);
        }
      }
    } catch (e) {
      console.error('Unable to get clips for clipgroup', { e });
    } finally {
      setProgress(false);
    }
  };

  useEffect(() => {
    const steps: Array<Step> = [infoStep, contentStep];
    setAvailableSteps(steps);
    if (assetForm && assetForm.mode === 'existing' && assetForm.id) {
      setRightOpen(assetForm.isLongFormContentEnabled);
      populateLongFormFields(assetForm);
      getClips();
    }
    return () => {
      // console.log('cleaned up clipgroup');
    };
  }, []);

  useEffect(() => {}, [assetForm]);

  return (
    <Dialog open={open} fullScreen>
      <Box className="step-container clip-group" id="clip-group">
        {/* <CssBaseline /> */}
        <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={handleDrawerClose} aria-label="close">
              <CloseIcon />
            </IconButton>
            <Typography className="cliptool-heading" noWrap component="div" title="Clip Group" sx={{ flexGrow: 1 }}>
              Clip Group : {assetForm.title}
            </Typography>
            <IconButton color="inherit" onClick={expandRightSide}>
              {rightOpen && <ChevronLeftIcon />}
              {!rightOpen && <ChevronRightIcon />}
            </IconButton>
          </Toolbar>
        </AppBar>
        <Grid container className="step-grid-container">
          <Grid item xs={rightOpen ? 8 : 12}>
            <div className="step-header-container">
              <Stepper nonLinear activeStep={activeStep}>
                {availableSteps.map((label, index) => (
                  <Step key={label.id} completed={completed[index]}>
                    <StepButton color="inherit" onClick={assetFormValues.handleSubmit(handleStep(index))}>
                      {label.title}
                    </StepButton>
                  </Step>
                ))}
              </Stepper>
            </div>
            <Box className="step-body-container">
              <>
                <FormProvider {...assetFormValues}>
                  <Box className="step-content-container">
                    {getStepContent(currentStep)}
                    {openAssetDetailsModal && (
                      <AssetDetailsModal
                        open={openAssetDetailsModal}
                        onClose={() => setOpenAssetDetailsModal(false)}
                        processAssetsReturnedFromSaving={refreshAssets}
                      />
                    )}
                  </Box>
                  <Box className="step-button-container">
                    <Box sx={{ flex: '1 1 auto' }} />
                    <Button color="primary" variant="contained" disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ mr: 1 }}
                      disabled={isLastStep()}
                      onClick={assetFormValues.handleSubmit(handleNext)}
                    >
                      Next
                    </Button>
                    <Button
                      variant="contained"
                      sx={{ mr: 1 }}
                      color="primary"
                      onClick={assetFormValues.handleSubmit(onSaveClick)}
                    >
                      Save / Update
                    </Button>
                  </Box>
                </FormProvider>
              </>
            </Box>
          </Grid>
          {rightOpen && (
            <Grid item xs={4} sx={{ borderLeft: '1px solid #ddd' }}>
              <Box sx={{ padding: 3, marginTop: '80px' }} className="metadata-section">
                <MetadataContent
                  tagState={tagState}
                  isEditingDisabled={false}
                  onInputChange={onInputChange}
                  onSingleTagFieldBlur={onSingleTagFieldBlur}
                  onMultipleTagFieldBlur={onMultipleTagFieldBlur}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </Dialog>
  );
};

export default ClipGroupModal;
